import { useState, useRef, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, X, CornerDownLeft } from "lucide-react";
import useCalcSearch from "@/hooks/useCalcSearch";

export default function SearchBar({ compact = false }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const ref = useRef(null);
  const navigate = useNavigate();
  const results = useCalcSearch(q);

  // Close on outside click; open via ⌘K
  useEffect(() => {
    const outside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const keyOpen = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        ref.current?.querySelector("input")?.focus();
      }
    };
    document.addEventListener("mousedown", outside);
    document.addEventListener("keydown", keyOpen);
    return () => {
      document.removeEventListener("mousedown", outside);
      document.removeEventListener("keydown", keyOpen);
    };
  }, []);

  const commit = useCallback((slug) => { navigate(`/${slug}`); setQ(""); setOpen(false); }, [navigate]);

  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(results.length - 1, a + 1)); return; }
    if (e.key === "ArrowUp")   { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); return; }
    if (e.key === "Enter" && results[active]) { e.preventDefault(); commit(results[active].slug); return; }
    if (e.key === "Escape") setOpen(false);
  };

  return (
    <div ref={ref} className={`relative ${compact ? "w-full" : "w-full max-w-xl"}`} data-testid="search-bar-container">
      <SearchInput
        value={q}
        onChange={(v) => { setQ(v); setOpen(true); setActive(0); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        onClear={() => { setQ(""); setOpen(false); }}
      />
      {open && q && (
        <SearchResults results={results} active={active} onPick={commit} query={q} />
      )}
    </div>
  );
}

function SearchInput({ value, onChange, onFocus, onKeyDown, onClear }) {
  return (
    <div className="relative flex items-center">
      <Search className="absolute left-3 h-4 w-4 text-muted-foreground pointer-events-none" strokeWidth={1.75} />
      <input
        data-testid="global-search-input"
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        placeholder="Search calculators… (⌘K)"
        className="w-full h-11 pl-10 pr-10 rounded-md border border-border bg-card text-sm outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition"
      />
      {value && (
        <button
          data-testid="search-clear-btn"
          aria-label="Clear"
          onClick={onClear}
          className="absolute right-3 text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

function SearchResults({ results, active, onPick, query }) {
  if (results.length === 0) {
    return (
      <div className="absolute z-50 mt-2 w-full rounded-md border border-border bg-popover shadow-lg p-4 text-sm text-muted-foreground" data-testid="search-empty">
        No calculators match &quot;{query}&quot;
      </div>
    );
  }
  return (
    <div className="absolute z-50 mt-2 w-full rounded-md border border-border bg-popover shadow-lg overflow-hidden" data-testid="search-results-dropdown">
      {results.map((r, i) => (
        <SearchResultRow key={r.slug} row={r} activeRow={i === active} onPick={() => onPick(r.slug)} />
      ))}
    </div>
  );
}

function SearchResultRow({ row, activeRow, onPick }) {
  return (
    <Link
      to={`/${row.slug}`}
      data-testid={`search-result-${row.slug}`}
      onClick={onPick}
      className={`flex items-center gap-3 px-3 py-3 text-sm border-b border-border/60 last:border-b-0 ${activeRow ? "bg-accent" : "hover:bg-accent/60"}`}
    >
      <row.Icon className="h-4 w-4 text-primary shrink-0" strokeWidth={1.75} />
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{row.title}</div>
        <div className="text-xs text-muted-foreground truncate">{row.description}</div>
      </div>
      {activeRow && <CornerDownLeft className="h-3.5 w-3.5 text-muted-foreground" />}
    </Link>
  );
}
